import { Injectable } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, NavigationEnd, PRIMARY_OUTLET, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { filter, map, mergeMap } from "rxjs/operators";
import { PlatformService } from "./platform.service";

@Injectable({
  providedIn: "root",
})
export class PageTitleService {

  constructor(
    private titleService: Title,
    private translate: TranslateService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private platformService: PlatformService,
  ) {
    this.titleService.setTitle("");
  }

  initialize() {
    this.updateFavicon();
    this.updateManifest();
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        filter(route => route.outlet === PRIMARY_OUTLET),
        mergeMap(route => route.data),
      )
      .subscribe(event => {
        this.updateFavicon();
        this.updateManifest();
        if (event.title) {
          this.updateTitle(event.title);
        } else {
          this.resetTitle();
        }
      });
    this.translate.onLangChange.subscribe(() => {
      setTimeout(() => {
        let key = this.getCurrentRouteTitle();
        if (key !== "" && key !== undefined) {
          this.updateTitle(this.getCurrentRouteTitle());
        }
      });
    });
  }

  private updateFavicon(): void {
    const brand = this.platformService.isPaidByLandingFlow() ? "wellvia" : "epak";
    const svg = document.getElementById("favicon-svg") as HTMLLinkElement | null;
    const ico = document.getElementById("favicon-ico") as HTMLLinkElement | null;
    const apple = document.getElementById("apple-touch") as HTMLLinkElement | null;
    if (svg) svg.href = `assets/icons/${brand}/favicon.svg`;
    if (ico) ico.href = `assets/icons/${brand}/favicon.ico`;
    if (apple) apple.href = `assets/icons/${brand}/apple-touch-icon.png`;
  }

  private updateManifest(): void {
    const manifest = document.getElementById("app-manifest") as HTMLLinkElement | null;
    if (manifest) manifest.href = this.platformService.isPaidByLandingFlow() ? "wellvia-manifest.json" : "manifest.json";
  }

  private updateTitle(titleKey: string) {
    this.translate.get(titleKey).subscribe(titleTranslation => {
      const fullTitle = `${titleTranslation} | ${this.platformService.brandName()}`;
      this.titleService.setTitle(`${fullTitle}`);
      this.updateAriaLiveRegion(fullTitle);
    });
  }

  private resetTitle() {
    const appTitle = this.titleService.getTitle();
    this.titleService.setTitle(appTitle);
    this.updateAriaLiveRegion(appTitle);
  }

  private updateAriaLiveRegion(title: string) {
    const ariaLiveRegion = document.getElementById("aria-live-region");
    if (ariaLiveRegion) {
      ariaLiveRegion.textContent = title;
    }
  }

  private getCurrentRouteTitle(): string {
    let route = this.activatedRoute;
    while (route.firstChild) route = route.firstChild;

    if (route.outlet === PRIMARY_OUTLET && route.snapshot.data.title) {
      return route.snapshot.data.title;
    }
    return "";
  }
}
